import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { RequesterHelpdeskRuntime } from '@features/helpdesk/embed';
import { Button } from '@shared/ui';
import type { HelpdeskWidgetConfiguration } from '../model/config';
import { HelpdeskContent } from './HelpdeskContent';
import launcherImage from '../assets/help-launcher.svg?inline';
import { WidgetSession, type WidgetSessionSnapshot } from '../model/widget-session';
import { BubbleSession, type BubbleSnapshot } from '../model/bubble-session';
import { connectMovePopup } from '../model/popup-connection';

export function HelpdeskWidget({
  configuration,
  runtime,
  configurationError,
  session,
  sessionSnapshot,
  bubble,
  bubbleSnapshot,
  actorKey,
}: {
  configuration: HelpdeskWidgetConfiguration;
  runtime: RequesterHelpdeskRuntime;
  configurationError?: string;
  session: WidgetSession | null;
  sessionSnapshot: WidgetSessionSnapshot;
  bubble: BubbleSession | null;
  bubbleSnapshot: BubbleSnapshot;
  actorKey: string;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [open, setOpen] = useState(configuration.initialOpen === true);
  const [connectionBusy, setConnectionBusy] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const connectionEpoch = useRef(0);
  const launcher = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const close = useCallback(() => {
    connectionEpoch.current += 1;
    setConnectionBusy(false);
    setConnectionError(null);
    bubble?.deactivate();
    setOpen(false);
    void client.cancelQueries();
    client.clear();
  }, [client, bubble]);
  const connect = async () => {
    if (!session || connectionBusy) return;
    const epoch = connectionEpoch.current;
    const moveGeneration = session.snapshot().generation;
    setConnectionBusy(true);
    setConnectionError(null);
    try {
      const issued = await connectMovePopup({
        moveWebOrigin: new URL(configuration.supportUrl).origin,
        installationOrigin: window.location.origin,
        locale: configuration.locale,
      });
      if (connectionEpoch.current === epoch && session.snapshot().generation === moveGeneration)
        session.accept(issued);
    } catch (error) {
      if (connectionEpoch.current === epoch)
        setConnectionError(error instanceof Error ? error.message : 'Move connection failed.');
    } finally {
      if (connectionEpoch.current === epoch) setConnectionBusy(false);
    }
  };
  useEffect(() => () => { connectionEpoch.current += 1; }, []);
  const show = () => {
    void client.cancelQueries();
    client.clear();
    bubble?.activate();
    setOpen(true);
  };
  useEffect(() => {
    if (configuration.initialOpen) bubble?.activate();
  }, [bubble, configuration.initialOpen]);
  useEffect(() => {
    const element = panel.current;
    if (!open || element === null) return;
    const launcherElement = launcher.current;
    element.showModal();
    element.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const cancel = (event: Event) => {
      event.preventDefault();
      close();
    };
    element.addEventListener('cancel', cancel);
    return () => {
      element.removeEventListener('cancel', cancel);
      element.close();
      launcherElement?.focus();
      document.body.style.overflow = previousOverflow;
    };
  }, [close, open]);

  return (
    <div className="font-sans text-sm text-foreground antialiased">
      {open ? (
        <dialog
          ref={panel}
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="fixed inset-x-0 bottom-[30px] top-[30px] z-50 mx-auto my-0 h-[calc(100dvh-60px)] max-h-[calc(100dvh-60px)] w-full max-w-[800px] flex-col overflow-hidden border bg-background p-0 text-foreground backdrop:bg-black/40 open:flex sm:w-[calc(100vw-20px)]"
        >
          <HelpdeskContent
            key={actorKey}
            configuration={configuration}
            runtime={runtime}
            configurationError={configurationError}
            session={session}
            sessionSnapshot={sessionSnapshot}
            bubble={bubble}
            bubbleSnapshot={bubbleSnapshot}
            onConnect={connect}
            connectionBusy={connectionBusy}
            connectionError={connectionError}
            headingId={titleId}
            onClose={close}
          />
        </dialog>
      ) : null}
      <Button
        ref={launcher}
        type="button"
        onClick={open ? close : show}
        aria-label={t('helpdesk.widget.title', 'Support')}
        aria-expanded={open}
        variant="ghost"
        className="fixed bottom-20 right-4 z-50 h-[50px] w-[50px] rounded-full border-0 bg-transparent p-0 sm:bottom-4"
      >
        <img src={launcherImage} alt="" aria-hidden="true" className="h-[50px] w-[50px]" />
      </Button>
    </div>
  );
}
